import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, FileText, AlertTriangle } from 'lucide-react';

export default function AgreementReviewPanel({ introducerId, introducerName, onClose, onDecision }) {
  const [agreement,   setAgreement]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [rejectReason,setRejectReason]= useState('');
  const [showReject,  setShowReject]  = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [idUploading, setIdUploading] = useState(false);
  const [idError,     setIdError]     = useState('');
  const [idSuccess,   setIdSuccess]   = useState('');
  const [dlFile,      setDlFile]      = useState(null);
  const [ppFile,      setPpFile]      = useState(null);
  const [tab,         setTab]         = useState('details');
  const [showReplaceForm, setShowReplaceForm] = useState(false);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`/api/agreement-review/${introducerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setAgreement(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [introducerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const safeParse = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return null; }
  };

  const handleApprove = async () => {
    const ref = window.prompt(
      'Confirm your network compliance team has AUTHORISED this introducer.\n\nAuthorisation reference (optional):'
    );
    if (ref === null) return; // cancelled
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/agreement-review/${introducerId}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ network_ref: ref.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to approve');
      onDecision('approved');
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const handleCountersign = async () => {
    if (!window.confirm('Countersign this agreement? Your name and today’s date will be added to the adviser signature line on the PDF.')) return;
    setSubmitting(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/agreement-review/${introducerId}/countersign`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to countersign');
      await load();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const handleSendCompliance = async () => {
    if (!window.confirm('Mark this agreement as sent to your network compliance team?\n\nDownload the countersigned PDF first if you haven’t already.')) return;
    setSubmitting(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/agreement-review/${introducerId}/send-compliance`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      await load();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/agreement-review/${introducerId}/reject`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason })
      });
      if (!res.ok) throw new Error('Failed to reject');
      onDecision('rejected');
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const regeneratePDF = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/agreement-review/${introducerId}/generate-pdf`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Regeneration failed');
      await load();
      alert('PDF regenerated.');
    } catch (err) { alert('Regenerate failed: ' + err.message); }
    finally { setPdfLoading(false); }
  };

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`/api/agreement-review/${introducerId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert('PDF not yet generated — try again in a few seconds.'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Agreement_${introducerName.replace(/[^a-zA-Z0-9]/g,'_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed: ' + err.message); }
    finally { setPdfLoading(false); }
  };

  const uploadID = async () => {
    if (!dlFile && !ppFile) return setIdError('Please select at least one document');
    setIdUploading(true); setIdError(''); setIdSuccess('');
    try {
      const token = localStorage.getItem('token');
      const fd    = new FormData();
      if (dlFile) fd.append('driving_licence', dlFile);
      if (ppFile) fd.append('passport', ppFile);
      const res  = await fetch(`/api/agreements/upload-id-broker/${introducerId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setIdSuccess('Documents uploaded ✓');
      setDlFile(null); setPpFile(null);
      const res2  = await fetch(`/api/agreement-review/${introducerId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data2 = await res2.json();
      setAgreement(data2);
    } catch (err) { setIdError(err.message); }
    finally { setIdUploading(false); }
  };

  // ── Data resolution ─────────────────────────────────────────────────────────
  const dynamicFormData   = safeParse(agreement?.dynamic_form_data) || null;
  const hasCustomTemplate = !!(agreement?.template_id && dynamicFormData);

  // Old-style: stored as declaration_answers array
  const oldDeclarations = safeParse(agreement?.declaration_answers) || [];

  // New-style: extract from dynamic_form_data using template fields
  const newDeclarations = (() => {
    if (!hasCustomTemplate || oldDeclarations.length > 0) return [];
    const templateFields = safeParse(agreement?.template_extracted_fields) || [];
    return templateFields
      .filter(f => f.type === 'select' && f.options?.includes('Yes') && f.options?.includes('No'))
      .map(f => ({
        question: f.label,
        answer:   dynamicFormData[f.key] === 'Yes',
        details:  dynamicFormData['additional_details'] || dynamicFormData['additional_information'] || ''
      }));
  })();

  const allDeclarations = oldDeclarations.length > 0 ? oldDeclarations : newDeclarations;
  const yesDeclarations = allDeclarations.filter(d => d.answer === true || d.answer === 'Yes');

  // Old-style personal/firm details
  const introDetails = safeParse(agreement?.introducer_details) || {};
  const firmDetails  = safeParse(agreement?.firm_details) || {};

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Agreement Review</h2>
            <p style={styles.sub}>{introducerName}</p>
          </div>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        {loading && <div style={styles.center}>Loading agreement...</div>}
        {error   && <div style={styles.errorBox}>{error}</div>}

        {agreement && (
          <>
            {/* Status */}
            <div style={{ ...styles.statusBar, ...(agreement.status==='approved' ? styles.statusApproved : agreement.status==='rejected' ? styles.statusRejected : styles.statusPending) }}>
              {agreement.status === 'pending'         && '⏳ 1 of 3 · Awaiting your countersignature'}
              {agreement.status === 'countersigned'   && '✍️ 2 of 3 · Countersigned — send to your network compliance team'}
              {agreement.status === 'with_compliance' && '📤 3 of 3 · With network compliance — approve once authorised'}
              {agreement.status === 'approved'        && `✅ Approved${agreement.network_ref ? ` · ref ${agreement.network_ref}` : ''}`}
              {agreement.status === 'rejected'        && '❌ Rejected'}
              {agreement.id_upload_skipped && <span style={styles.idWarning}>⚠️ ID not yet uploaded</span>}
            </div>

            {/* Unapproved form layout — PDFs come out unfilled until it's approved */}
            {agreement.template_id && agreement.template_mapping_status !== 'approved' && (
              <div style={{ padding:'10px 24px', background:'#fffbeb', borderBottom:'1px solid #fcd34d', fontSize:12.5, color:'#92400e' }}>
                ⚠️ This template's form layout hasn't been approved, so this PDF shows the blank form with the
                answers on the certificate pages. Approve the layout in Settings → Agreements, then use Regenerate here.
              </div>
            )}

            {/* PDF download */}
            {agreement.agreement_pdf_path && (
              <div style={{ padding:'10px 24px', background:'#f0fdf4', borderBottom:'1px solid #bbf7d0', display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:13, color:'#166534' }}>📄 Completed agreement PDF ready</span>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={regeneratePDF} disabled={pdfLoading}
                    style={{ padding:'6px 14px', borderRadius:6, border:'1px solid #86efac', background:'#fff', color:'#166534', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    {pdfLoading ? 'Working...' : '↻ Regenerate'}
                  </button>
                  <button onClick={downloadPDF} disabled={pdfLoading}
                    style={{ padding:'6px 14px', borderRadius:6, border:'none', background:'#10b981', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    {pdfLoading ? 'Downloading...' : '⬇ Download PDF'}
                  </button>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div style={styles.tabs}>
              {['details','firm','declarations','id'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ ...styles.tab, ...(tab===t ? styles.tabActive : {}) }}>
                  {t==='details' ? 'Personal' : t==='firm' ? 'Firm' : t==='declarations' ? `Declarations${yesDeclarations.length>0 ? ` (${yesDeclarations.length} YES)` : ''}` : 'ID & Docs'}
                </button>
              ))}
            </div>

            <div style={styles.body}>

              {/* Personal / Dynamic */}
              {tab === 'details' && (
                <div>
                  {hasCustomTemplate ? (
                    <DynamicDataView formData={dynamicFormData} agreement={agreement} />
                  ) : (
                    <div>
                      {[
                        ['Full Name',  introDetails.name],
                        ['Date of Birth', introDetails.dob],
                        ['Address',    introDetails.address],
                        ['Telephone',  introDetails.phone],
                        ['Mobile',     introDetails.mobile],
                        ['Email',      introDetails.email],
                        ['Signed As',  agreement.esign_name],
                        ['Signed At',  agreement.signed_at ? new Date(agreement.signed_at).toLocaleString('en-GB') : '-'],
                        ['IP Address', agreement.ip_address],
                      ].map(([label, value]) => (
                        <div key={label} style={styles.infoRow}>
                          <span style={styles.infoLabel}>{label}</span>
                          <span style={styles.infoValue}>{value || '-'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={styles.feeBox}>
                    <p style={styles.feeLabel}>Fee Arrangement</p>
                    <p style={styles.feeValue}>
                      {agreement.fee_value
                        ? (agreement.fee_type === 'percentage' ? `${agreement.fee_value}% of commission` : `£${agreement.fee_value} fixed fee`)
                        : 'Not specified'}
                      {agreement.fee_value && agreement.fee_applies_to_renewals ? ' (incl. renewals)' : ''}
                    </p>
                    <p style={styles.feeLabel}>Adviser</p>
                    <p style={styles.feeValue}>{agreement.adviser_name || '-'} {agreement.frn_number ? `(FRN: ${agreement.frn_number})` : ''}</p>
                  </div>
                </div>
              )}

              {/* Firm */}
              {tab === 'firm' && (
                <div>
                  {hasCustomTemplate ? (
                    // For dynamic forms, extract firm fields from dynamic_form_data
                    <DynamicFirmView formData={dynamicFormData} />
                  ) : (
                    [
                      ['Firm Name',          firmDetails.firm_name],
                      ['Firm Address',       firmDetails.firm_address],
                      ['Telephone',          firmDetails.phone],
                      ['Nature of Business', firmDetails.nature_of_business],
                    ].map(([label, value]) => (
                      <div key={label} style={styles.infoRow}>
                        <span style={styles.infoLabel}>{label}</span>
                        <span style={styles.infoValue}>{value || '-'}</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Declarations */}
              {tab === 'declarations' && (
                <div>
                  {yesDeclarations.length > 0 && (
                    <div style={styles.warningBox}>
                      <AlertTriangle size={16} style={{ marginRight:8 }} />
                      {yesDeclarations.length} question{yesDeclarations.length > 1 ? 's' : ''} answered YES — review carefully
                    </div>
                  )}
                  {allDeclarations.length === 0 && (
                    <p style={{ fontSize:13, color:'#9ca3af', padding:16 }}>No declaration answers recorded.</p>
                  )}
                  {allDeclarations.map((d, i) => {
                    const isYes = d.answer === true || d.answer === 'Yes';
                    return (
                      <div key={i} style={{ ...styles.declRow, ...(isYes ? styles.declYes : {}) }}>
                        <div style={styles.declQ}>{d.question}</div>
                        <div style={{ ...styles.declAns, color: isYes ? '#dc2626' : '#16a34a', fontWeight:700 }}>
                          {isYes ? 'YES' : 'NO'}
                        </div>
                      </div>
                    );
                  })}
                  {allDeclarations[0]?.details && (
                    <div style={styles.detailsBox}>
                      <p style={styles.infoLabel}>Additional Details Provided</p>
                      <p style={{ fontSize:13, color:'#374151', margin:0 }}>{allDeclarations[0].details}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ID Documents */}
              {tab === 'id' && (
                <div>
                  {[
                    ['Driving Licence', agreement.driving_licence_path, 'driving_licence'],
                    ['Passport',        agreement.passport_path, 'passport'],
                  ].map(([label, path, docType]) => (
                    <div key={label} style={styles.docRow}>
                      <FileText size={16} style={{ color: path ? '#10b981' : '#9ca3af' }} />
                      <span style={styles.docLabel}>{label}</span>
                      <span style={{ fontSize:13, color: path ? '#10b981' : '#9ca3af' }}>
                        {path ? '✓ Uploaded' : 'Not uploaded'}
                      </span>
                      {path && (
                        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
                          <button onClick={() => window.open(`/api/agreements/download-id/${introducerId}/${docType}`, '_blank')}
                            style={{ padding:'5px 12px', fontSize:12, background:'#3b82f6', color:'#fff', border:'none', borderRadius:4, cursor:'pointer' }}>
                            📥 Download
                          </button>
                          <button onClick={() => { if (docType === 'driving_licence') setDlFile(null); else setPpFile(null); setShowReplaceForm(true); }}
                            style={{ padding:'5px 12px', fontSize:12, background:'#f59e0b', color:'#fff', border:'none', borderRadius:4, cursor:'pointer' }}>
                            🔄 Replace
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {(agreement.id_upload_skipped || !agreement.driving_licence_path || !agreement.passport_path || showReplaceForm) && !idSuccess && (
                    <div style={{ marginTop:16, padding:14, background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:8 }}>
                      <p style={{ margin:'0 0 6px', fontSize:13, fontWeight:700, color:'#92400e' }}>⚠️ Upload ID on behalf of introducer</p>
                      <p style={{ margin:'0 0 12px', fontSize:12, color:'#78350f' }}>The introducer has not uploaded their ID. You can upload it here.</p>
                      {[['Driving Licence', dlFile, setDlFile], ['Passport', ppFile, setPpFile]].map(([lbl, file, setFile]) => (
                        <div key={lbl}>
                          <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>{lbl}</label>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])}
                            style={{ fontSize:12, marginBottom:4, display:'block', width:'100%' }} />
                          {file && <p style={{ fontSize:11, color:'#10b981', margin:'0 0 8px' }}>✓ {file.name}</p>}
                        </div>
                      ))}
                      {idError && <p style={{ fontSize:12, color:'#dc2626', margin:'0 0 8px' }}>{idError}</p>}
                      <button onClick={uploadID} disabled={idUploading}
                        style={{ width:'100%', padding:'9px', borderRadius:8, border:'none',
                          background:'#f59e0b', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                        {idUploading ? 'Uploading...' : '⬆ Upload Documents'}
                      </button>
                      {showReplaceForm && (
                        <button onClick={() => { setShowReplaceForm(false); setDlFile(null); setPpFile(null); setIdError(''); }}
                          style={{ width:'100%', marginTop:8, padding:'9px', borderRadius:8, border:'1px solid #d1d5db', 
                            background:'transparent', color:'#374151', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                  {idSuccess && <p style={{ fontSize:13, color:'#10b981', marginTop:12, fontWeight:600 }}>{idSuccess}</p>}
                </div>
              )}

            </div>

            {/* Actions — three-stage flow: countersign → send to compliance → approve */}
            {['pending', 'countersigned', 'with_compliance'].includes(agreement.status) && (
              <div style={styles.actions}>
                {!showReject ? (
                  <>
                    <button onClick={() => setShowReject(true)} style={styles.rejectBtn} disabled={submitting}>
                      <XCircle size={16} /> Reject
                    </button>
                    {agreement.status === 'pending' && (
                      <button onClick={handleCountersign} style={styles.approveBtn} disabled={submitting}>
                        <CheckCircle size={16} /> {submitting ? 'Signing...' : '✍️ Countersign'}
                      </button>
                    )}
                    {agreement.status === 'countersigned' && (
                      <button onClick={handleSendCompliance} style={styles.approveBtn} disabled={submitting}>
                        <CheckCircle size={16} /> {submitting ? 'Updating...' : '📤 Mark sent to compliance'}
                      </button>
                    )}
                    {agreement.status === 'with_compliance' && (
                      <button onClick={handleApprove} style={styles.approveBtn} disabled={submitting}>
                        <CheckCircle size={16} /> {submitting ? 'Approving...' : 'Approve (network authorised)'}
                      </button>
                    )}
                  </>
                ) : (
                  <div style={{ width:'100%' }}>
                    <label style={styles.infoLabel}>Reason for rejection (optional)</label>
                    <textarea style={styles.textarea} rows={3} value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="e.g. Unable to verify identity documents" />
                    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                      <button onClick={() => setShowReject(false)} style={styles.cancelBtn}>Cancel</button>
                      <button onClick={handleReject} style={styles.rejectBtn} disabled={submitting}>
                        {submitting ? 'Rejecting...' : 'Confirm Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Dynamic personal data viewer ──────────────────────────────────────────────
function DynamicDataView({ formData, agreement }) {
  if (!formData) return <p style={{ fontSize:13, color:'#9ca3af' }}>No form data available.</p>;

  // Show personal fields — exclude firm/declaration/additional fields
  const FIRM_KEYS    = ['firm_name','introducing_firm_name','firm_address','introducing_firm_address','firm_telephone_number','firm_fax_number','nature_of_business','fax_number'];
  const EXCLUDE_KEYS = ['additional_details','additional_information','additional_details_instruction'];

  const personalEntries = Object.entries(formData).filter(([key]) => {
    const k = key.toLowerCase();
    if (FIRM_KEYS.some(fk => k.includes(fk))) return false;
    if (EXCLUDE_KEYS.some(ek => k.includes(ek))) return false;
    // Skip yes/no declaration answers (shown in Declarations tab)
    if (['yes','no'].includes(String(formData[key]).toLowerCase()) && key.length > 10) return false;
    return true;
  });

  return (
    <div>
      <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'12px 16px', marginBottom:16 }}>
        <p style={{ fontSize:11, color:'#6b7280', fontWeight:600, textTransform:'uppercase', margin:'0 0 8px' }}>Signature Details</p>
        <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
          <div>
            <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px' }}>SIGNED AS</p>
            <p style={{ fontSize:15, fontStyle:'italic', color:'#1a365d', fontWeight:600, margin:0 }}>{agreement.esign_name}</p>
          </div>
          <div>
            <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px' }}>DATE</p>
            <p style={{ fontSize:13, color:'#065f46', margin:0 }}>{new Date(agreement.signed_at).toLocaleString('en-GB')}</p>
          </div>
          <div>
            <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px' }}>IP ADDRESS</p>
            <p style={{ fontSize:13, color:'#065f46', margin:0 }}>{agreement.ip_address}</p>
          </div>
        </div>
      </div>
      {personalEntries.map(([key, value]) => {
        const label   = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || '—');
        return (
          <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
            padding:'8px 0', borderBottom:'1px solid #f3f4f6', gap:12 }}>
            <span style={{ fontSize:12, color:'#9ca3af', fontWeight:600, textTransform:'uppercase',
              letterSpacing:'0.04em', flex:'0 0 45%' }}>{label}</span>
            <span style={{ fontSize:13, color:'#0e1b2c', textAlign:'right', flex:1 }}>{display}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Dynamic firm data viewer ──────────────────────────────────────────────────
function DynamicFirmView({ formData }) {
  if (!formData) return <p style={{ fontSize:13, color:'#9ca3af' }}>No firm data available.</p>;

  const FIRM_KEYS = ['firm_name','introducing_firm_name','firm_address','introducing_firm_address',
    'firm_telephone','firm_fax','fax_number','nature_of_business','firm_telephone_number','firm_fax_number'];

  const firmEntries = Object.entries(formData).filter(([key]) => {
    const k = key.toLowerCase();
    return FIRM_KEYS.some(fk => k.includes(fk));
  });

  if (firmEntries.length === 0) return <p style={{ fontSize:13, color:'#9ca3af' }}>No firm details recorded.</p>;

  return (
    <div>
      {firmEntries.map(([key, value]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
          <div key={key} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6', gap:12 }}>
            <span style={{ fontSize:12, color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', flex:'0 0 45%' }}>{label}</span>
            <span style={{ fontSize:13, color:'#0e1b2c', textAlign:'right', flex:1 }}>{value || '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  overlay:      { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', justifyContent:'flex-end' },
  panel:        { background:'#fff', width:520, maxWidth:'95vw', height:'100vh', display:'flex', flexDirection:'column', boxShadow:'-8px 0 32px rgba(0,0,0,0.15)' },
  header:       { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'24px 24px 16px', borderBottom:'1px solid #e5e7eb' },
  title:        { fontSize:18, fontWeight:700, color:'#0e1b2c', margin:'0 0 4px' },
  sub:          { fontSize:13, color:'#6b7280', margin:0 },
  closeBtn:     { background:'none', border:'none', cursor:'pointer', color:'#6b7280', padding:4 },
  statusBar:    { padding:'10px 24px', fontSize:13, fontWeight:600, display:'flex', justifyContent:'space-between', alignItems:'center' },
  statusPending:  { background:'#fef3c7', color:'#92400e' },
  statusApproved: { background:'#d1fae5', color:'#065f46' },
  statusRejected: { background:'#fee2e2', color:'#991b1b' },
  idWarning:    { fontSize:12, color:'#f59e0b', fontWeight:600 },
  tabs:         { display:'flex', borderBottom:'1px solid #e5e7eb', padding:'0 24px' },
  tab:          { padding:'10px 14px', border:'none', background:'none', cursor:'pointer', fontSize:13, color:'#6b7280', borderBottom:'2px solid transparent', marginBottom:-1 },
  tabActive:    { color:'#10b981', borderBottomColor:'#10b981', fontWeight:600 },
  body:         { flex:1, overflowY:'auto', padding:24 },
  infoRow:      { display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6' },
  infoLabel:    { fontSize:12, color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' },
  infoValue:    { fontSize:14, color:'#0e1b2c', textAlign:'right', maxWidth:'60%' },
  feeBox:       { background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'12px 16px', marginTop:16 },
  feeLabel:     { fontSize:11, color:'#6b7280', fontWeight:600, textTransform:'uppercase', margin:'0 0 2px' },
  feeValue:     { fontSize:14, color:'#065f46', fontWeight:600, margin:'0 0 10px' },
  warningBox:   { display:'flex', alignItems:'center', background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400e', marginBottom:16 },
  declRow:      { padding:'10px 0', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 },
  declYes:      { background:'#fef2f2', padding:'8px 10px', borderRadius:6, margin:'4px 0' },
  declQ:        { fontSize:13, color:'#374151', flex:1 },
  declAns:      { fontSize:13, minWidth:36, textAlign:'right' },
  detailsBox:   { background:'#f9fafb', borderRadius:8, padding:'12px 14px', marginTop:16 },
  docRow:       { display:'flex', alignItems:'center', gap:10, padding:'12px 0', borderBottom:'1px solid #f3f4f6' },
  docLabel:     { flex:1, fontSize:14, color:'#374151' },
  actions:      { padding:'16px 24px', borderTop:'1px solid #e5e7eb', display:'flex', gap:10, justifyContent:'flex-end' },
  approveBtn:   { display:'flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:8, border:'none', background:'#10b981', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 },
  rejectBtn:    { display:'flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:8, border:'none', background:'#fee2e2', color:'#dc2626', cursor:'pointer', fontSize:14, fontWeight:600 },
  cancelBtn:    { padding:'8px 16px', borderRadius:8, border:'1px solid #e5e7eb', background:'#fff', cursor:'pointer', fontSize:13 },
  textarea:     { width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #d1d5db', fontSize:13, boxSizing:'border-box', resize:'vertical' },
  center:       { padding:24, textAlign:'center', color:'#6b7280' },
  errorBox:     { margin:16, padding:'10px 14px', background:'#fee2e2', borderRadius:8, fontSize:13, color:'#dc2626' },
};
